import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  scanService,
  type ScanResult as ScanResultType,
  type GradingResult,
  type CorrectionItem,
} from "@/services/scan.service";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  RefreshCcw,
  Save,
  Camera,
  Clock,
} from "lucide-react";

// ============================================================================
// Constants
// ============================================================================

const POLL_INTERVAL = 2000; // 2 seconds

// ============================================================================
// Helper Functions
// ============================================================================

function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error && typeof error === "object") {
    const err = error as { code?: string; name?: string };
    if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
      return true;
    }
  }
  return false;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ============================================================================
// Components
// ============================================================================

function LoadingSkeleton() {
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-9 w-64" />
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function ProcessingState({ status }: { status: string }) {
  return (
    <div className="container mx-auto p-6">
      <Link
        to="/scan"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Scans
      </Link>

      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === "pending" ? (
              <Clock className="h-16 w-16 text-muted-foreground animate-pulse" />
            ) : (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
          </div>
          <CardTitle>
            {status === "pending" ? "Starting Processing..." : "Processing Worksheet"}
          </CardTitle>
          <CardDescription>
            {status === "pending"
              ? "Preparing to extract answers from your worksheet..."
              : "Extracting answers and grading. This may take a moment."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress value={status === "pending" ? 10 : 50} className="h-2" />
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="container mx-auto p-6">
      <Link
        to="/scan"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Scans
      </Link>

      <Card className="max-w-md mx-auto border-destructive/50">
        <CardHeader className="text-center">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <CardTitle>Processing Failed</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={onRetry} className="w-full">
            <RefreshCcw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button variant="outline" asChild className="w-full">
            <Link to="/scan">
              <Camera className="mr-2 h-4 w-4" />
              Scan Another
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

interface QuestionResultProps {
  result: GradingResult;
  correction: CorrectionItem | undefined;
  onCorrectionChange: (correction: CorrectionItem) => void;
}

function QuestionResult({ result, correction, onCorrectionChange }: QuestionResultProps) {
  const [isOpen, setIsOpen] = useState(!result.isCorrect);

  const isOverridden = correction?.correct !== undefined;
  const displayCorrect = isOverridden ? correction.correct : result.isCorrect;
  const displayScore = correction?.score !== undefined ? correction.score : result.score;

  const handleMarkCorrect = () => {
    onCorrectionChange({
      questionId: result.questionId,
      correct: true,
      score: result.maxScore,
      feedback: correction?.feedback,
    });
  };

  const handleMarkIncorrect = () => {
    onCorrectionChange({
      questionId: result.questionId,
      correct: false,
      score: 0,
      feedback: correction?.feedback,
    });
  };

  const handleFeedbackChange = (feedback: string) => {
    onCorrectionChange({
      questionId: result.questionId,
      correct: correction?.correct,
      score: correction?.score,
      feedback: feedback || undefined,
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between p-4 h-auto"
          >
            <div className="flex items-center gap-3">
              {displayCorrect ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              <span className="font-medium">Question {result.questionId}</span>
              <Badge variant={displayCorrect ? "default" : "destructive"}>
                {displayScore}/{result.maxScore}
              </Badge>
              {isOverridden && (
                <Badge variant="outline" className="text-xs">
                  Modified
                </Badge>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Student Answer
                </p>
                <p className="text-sm bg-muted p-2 rounded">
                  {Array.isArray(result.extractedAnswer)
                    ? result.extractedAnswer.join(", ")
                    : result.extractedAnswer || "(empty)"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Expected Answer
                </p>
                <p className="text-sm bg-muted p-2 rounded">
                  {Array.isArray(result.expectedAnswer)
                    ? result.expectedAnswer.join(", ")
                    : result.expectedAnswer}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={displayCorrect ? "default" : "outline"}
                onClick={handleMarkCorrect}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />
                Mark Correct
              </Button>
              <Button
                size="sm"
                variant={!displayCorrect ? "destructive" : "outline"}
                onClick={handleMarkIncorrect}
              >
                <XCircle className="mr-1 h-4 w-4" />
                Mark Incorrect
              </Button>
            </div>

            <div>
              <label className="text-sm font-medium">Feedback (optional)</label>
              <Input
                placeholder="Add feedback for this question..."
                value={correction?.feedback || ""}
                onChange={(e) => handleFeedbackChange(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function ScanResult() {
  const { scanId } = useParams<{ scanId: string }>();

  const [scan, setScan] = useState<ScanResultType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, CorrectionItem>>({});
  const [saving, setSaving] = useState(false);

  // Refs
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const extractControllerRef = useRef<AbortController | null>(null);
  const fetchControllerRef = useRef<AbortController | null>(null);
  const saveControllerRef = useRef<AbortController | null>(null);

  // Cleanup polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Fetch scan data
  const fetchScan = useCallback(
    async (signal?: AbortSignal) => {
      if (!scanId) {
        setError("No scan ID provided");
        setLoading(false);
        return null;
      }

      try {
        const data = await scanService.getScan(scanId, signal);
        if (!signal?.aborted) {
          setScan(data);
          setError(null);
        }
        return data;
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load scan:", err);
          setError(err instanceof Error ? err.message : "Failed to load scan");
        }
        return null;
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [scanId]
  );

  // Trigger extraction
  const triggerExtraction = useCallback(async () => {
    if (!scanId) return;

    extractControllerRef.current?.abort();
    const controller = new AbortController();
    extractControllerRef.current = controller;

    setExtracting(true);

    try {
      const result = await scanService.extractScan(scanId, controller.signal);
      if (!controller.signal.aborted) {
        setScan(result);
        toast.success("Grading complete!");
      }
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("Extraction failed:", err);
        // Refresh to get the error status from the server
        await fetchScan(controller.signal);
      }
    } finally {
      setExtracting(false);
    }
  }, [scanId, fetchScan]);

  // Initial fetch and polling setup
  useEffect(() => {
    fetchControllerRef.current?.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    const init = async () => {
      const data = await fetchScan(controller.signal);

      if (!controller.signal.aborted && data) {
        // If pending, trigger extraction
        if (data.status === "pending") {
          triggerExtraction();
        }
        // If processing, start polling
        else if (data.status === "processing") {
          pollIntervalRef.current = setInterval(async () => {
            // Check if aborted before making request
            if (controller.signal.aborted) {
              stopPolling();
              return;
            }
            const updated = await fetchScan(controller.signal);
            if (updated && updated.status !== "processing") {
              stopPolling();
            }
          }, POLL_INTERVAL);
        }
      }
    };

    init();

    return () => {
      controller.abort();
      extractControllerRef.current?.abort();
      saveControllerRef.current?.abort();
      stopPolling();
    };
  }, [scanId, fetchScan, triggerExtraction, stopPolling]);

  // Handle correction change
  const handleCorrectionChange = useCallback((correction: CorrectionItem) => {
    setCorrections((prev) => ({
      ...prev,
      [correction.questionId]: correction,
    }));
  }, []);

  // Save corrections
  const handleSaveCorrections = async () => {
    if (!scanId) return;

    const correctionList = Object.values(corrections).filter(
      (c) => c.correct !== undefined || c.score !== undefined || c.feedback
    );

    if (correctionList.length === 0) {
      toast.info("No changes to save");
      return;
    }

    saveControllerRef.current?.abort();
    const controller = new AbortController();
    saveControllerRef.current = controller;

    setSaving(true);

    try {
      const result = await scanService.applyCorrections(
        scanId,
        correctionList,
        controller.signal
      );
      if (!controller.signal.aborted) {
        setScan(result);
        setCorrections({});
        toast.success("Corrections saved!");
      }
    } catch (err) {
      if (!isRequestCancelled(err)) {
        const message = err instanceof Error ? err.message : "Failed to save corrections";
        toast.error(message);
        console.error("Save failed:", err);
      }
    } finally {
      if (!controller.signal.aborted) {
        setSaving(false);
      }
    }
  };

  // Retry extraction
  const handleRetry = () => {
    setError(null);
    triggerExtraction();
  };

  // Loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error || (scan && scan.status === "failed")) {
    return (
      <ErrorState
        error={error || scan?.error || "Processing failed"}
        onRetry={handleRetry}
      />
    );
  }

  // Processing state
  if (!scan || scan.status === "pending" || scan.status === "processing" || extracting) {
    return <ProcessingState status={extracting ? "processing" : scan?.status || "pending"} />;
  }

  // Calculate current totals with corrections
  const hasCorrections = Object.keys(corrections).length > 0;
  let totalScore = scan.totalScore || 0;
  let maxScore = scan.maxScore || 0;

  if (hasCorrections && scan.gradingResults) {
    totalScore = scan.gradingResults.reduce((sum, r) => {
      const correction = corrections[r.questionId];
      if (correction?.score !== undefined) {
        return sum + correction.score;
      }
      return sum + r.score;
    }, 0);
  }

  const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <Link
          to="/scan"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Scans
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{scan.worksheetTitle}</h1>
            <p className="text-sm text-muted-foreground">
              Scanned {formatDate(scan.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {scan.requiresReview && !scan.reviewedByTeacher && (
              <Badge variant="secondary" className="gap-1">
                <AlertCircle className="h-3 w-3" />
                Needs Review
              </Badge>
            )}
            {scan.reviewedByTeacher && (
              <Badge variant="default" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Reviewed
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Score Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Score Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-4xl font-bold">
                {totalScore}
                <span className="text-2xl text-muted-foreground">/{maxScore}</span>
              </p>
              <p className="text-sm text-muted-foreground">Points</p>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{percentage}%</span>
                {hasCorrections && (
                  <span className="text-xs text-muted-foreground">(unsaved changes)</span>
                )}
              </div>
              <Progress value={percentage} className="h-3" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Results */}
      {scan.gradingResults && scan.gradingResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Question Results</CardTitle>
            <CardDescription>
              Review and adjust grading for each question
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {scan.gradingResults.map((result) => (
              <QuestionResult
                key={result.questionId}
                result={result}
                correction={corrections[result.questionId]}
                onCorrectionChange={handleCorrectionChange}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleSaveCorrections}
          disabled={!hasCorrections || saving}
          className="flex-1 sm:flex-none"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Corrections
            </>
          )}
        </Button>
        <Button variant="outline" asChild>
          <Link to="/scan">
            <Camera className="mr-2 h-4 w-4" />
            Scan Another
          </Link>
        </Button>
      </div>
    </div>
  );
}
